import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Popup, useMap, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const municipios = [
  { nome: "Tarumirim", cor: "#d73027" },
  { nome: "Engenheiro Caldas", cor: "#4575b4" },
  { nome: "Fernandes Tourinho", cor: "#1a9850" },
  { nome: "Sobralia", cor: "#984ea3" },
  { nome: "Alvarenga", cor: "#f46d43" }
];

const CACHE_KEY = "osm_limites_municipios_mg_v4";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const NOMINATIM_ENDPOINT = "https://nominatim.openstreetmap.org/search";

function normalizeName(v) {
  return (v || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}

function cacheLoad() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts || !parsed.data) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function cacheSave(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch (_) {}
}

async function fetchWithTimeout(url, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      signal: ctrl.signal,
      headers: {
        "Accept": "application/json"
      }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

async function fetchMunicipioNominatim(nome) {
  const q = encodeURIComponent(`${nome}, Minas Gerais, Brazil`);
  const url = `${NOMINATIM_ENDPOINT}?format=jsonv2&limit=1&polygon_geojson=1&q=${q}`;
  const data = await fetchWithTimeout(url, 25000);
  if (!Array.isArray(data) || !data.length) {
    throw new Error(`Sem resultado para ${nome}`);
  }
  return data[0];
}

const FitBounds = ({ layers }) => {
  const map = useMap();

  useEffect(() => {
    if (layers.length > 0) {
      const group = L.featureGroup(layers.map(l => L.geoJSON(l.geojson)));
      if (group.getLayers().length) {
        map.fitBounds(group.getBounds(), { padding: [20, 20] });
      }
    }
  }, [layers, map]);

  return null;
};


const OSMPage = () => {
  const [layers, setLayers] = useState([]);
  const [status, setStatus] = useState("Carregando...");
  const [statusColor, setStatusColor] = useState("#0a6f3c");
  const [legendItems, setLegendItems] = useState([]);
  
  // Responsive sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
         setIsSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadMap = async () => {
      const found = new Set();
      const errors = [];
      // let loadedLayers = [];

      try {
        const cached = cacheLoad();
        if (cached && Array.isArray(cached)) {
          // loadedLayers = cached;
          setLayers(cached);
          
          const items = cached.map(item => ({ nome: item.nome, cor: item.cor }));
          setLegendItems(items);
          items.forEach(item => found.add(normalizeName(item.nome)));
          
          setStatus(`Carregado do cache (${found.size}/${municipios.length})`);
        } else {
          const cacheItems = [];
          for (const municipio of municipios) {
            setStatus(`Buscando ${municipio.nome}...`);
            await new Promise(r => setTimeout(r, 1100)); // Respect API limits

            try {
              const result = await fetchMunicipioNominatim(municipio.nome);
              if (!result.geojson) throw new Error(`Sem geometria para ${municipio.nome}`);
              
              const item = { nome: municipio.nome, cor: municipio.cor, geojson: result.geojson };
              cacheItems.push(item);
              
              // Update state progressively
              setLayers(prev => [...prev, item]);
              setLegendItems(prev => [...prev, { nome: municipio.nome, cor: municipio.cor }]);
              
              found.add(normalizeName(municipio.nome));
              setStatus(`Carregando... ${found.size}/${municipios.length}`);
            } catch (innerError) {
                console.error(innerError);
                errors.push(`Erro ao carregar ${municipio.nome}: ${innerError.message}`);
            }
          }
          if (cacheItems.length > 0) {
             cacheSave(cacheItems);
          }
        }
      } catch (e) {
        const msg = e && e.message ? e.message : String(e);
        errors.push(msg);
      }

      // Check missing
      for (const m of municipios) {
        if (!found.has(normalizeName(m.nome))) errors.push(`Nao localizado: ${m.nome}`);
      }

      if (errors.length) {
        setStatusColor("#a94f09");
        setStatus(`Concluido com alertas (${found.size}/${municipios.length}).`);
        console.warn(errors);
      } else {
        setStatusColor("#0a6f3c");
        setStatus("Delimitacoes carregadas com sucesso.");
      }
    };

    loadMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const sidebarStyle = {
    padding: '16px', 
    borderRight: '1px solid #d8d8d8', 
    background: '#fff',
    width: '320px',
    height: '100%',
    overflowY: 'auto',
    position: isMobile ? 'absolute' : 'relative',
    left: 0,
    top: 0,
    zIndex: 1000,
    transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease-in-out',
    boxShadow: isMobile && isSidebarOpen ? '2px 0 8px rgba(0,0,0,0.2)' : 'none',
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', fontFamily: 'Arial, sans-serif', position: 'relative' }}>
        
        {/* Mobile Toggle Button (Close inside sidebar, Open outside) */}
        {isMobile && !isSidebarOpen && (
            <button onClick={toggleSidebar} style={{
                position: 'absolute', top: 10, left: 10, zIndex: 2000, 
                background: 'white', border: '1px solid #ccc', borderRadius: '4px', padding: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}>
                ☰ Menu
            </button>
        )}

      <aside style={sidebarStyle}>
        {isMobile && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button onClick={toggleSidebar} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            </div>
        )}
        <h1 style={{ margin: '0 0 8px', fontSize: '18px' }}>Limites Municipais (OSM)</h1>
        <p style={{ margin: '0 0 10px', color: '#4c4c4c', fontSize: '14px' }}>
          Tarumirim, Engenheiro Caldas, Fernandes Tourinho, Sobralia e Alvarenga.
        </p>
        <div style={{ fontSize: '14px', marginBottom: '10px', color: statusColor }}>
          {status}
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {legendItems.map((item, index) => (
            <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '14px' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '2px', border: '1px solid #222', display: 'inline-block', background: item.cor }}></span>
              {item.nome}
            </li>
          ))}
        </ul>
      </aside>
      <div style={{ flex: 1, position: 'relative', height: '100%' }}>
        <MapContainer center={[-19.2, -41.95]} zoom={10} style={{ height: '100%', width: '100%' }} zoomControl={false}>
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ZoomControl position="bottomright" />
            {layers.map((layer, index) => (
                <GeoJSON 
                    key={`${layer.nome}-${index}`} 
                    data={layer.geojson} 
                    style={{
                        color: layer.cor,
                        weight: 2.5,
                        fillColor: layer.cor,
                        fillOpacity: 0.18
                    }}
                >
                    <Popup>
                        <strong>{layer.nome}</strong>
                    </Popup>
                </GeoJSON>
            ))}
            <FitBounds layers={layers} />
        </MapContainer>
      </div>
    </div>
  );
};

export default OSMPage;
